import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core"
import { Resource } from "../../models/resource/resource"
import { CautionFlagsService } from "../../services/caution-flags.service"
import { IndexedDBService } from "../../services/index-DB.service"
import { takeUntil, tap } from "rxjs/operators"
import { IWindowResizeEvent, WindowSizeService } from "../../services/window-size.service"
import { asyncScheduler, Observable, Subject } from "rxjs"
import { Asset } from "../../models/resource/asset"
import { ResourceService } from "../../services/resource.service"
import { LessonBuilderService } from "../../services/lesson-builder.service"
import { resourcesToOpenInNewTab } from "../../models/constants"

@Component({
    selector: "app-sensitive-content-container",
    templateUrl: "sensitive-content.component.html",
    styleUrls: ["sensitive-content.component.scss"],
    standalone: false,
})
export class SensitiveContentComponent implements OnInit {
    @Input() actionButtonName: string = "Watch video"
    @Input() customClasses: string = ""
    @Input() customAction: boolean = false
    @Output() customActionTrigger: EventEmitter<boolean> = new EventEmitter<boolean>()
    private destroyed$: Subject<void> = new Subject<void>()

    isTablet: boolean
    innerWidth: number
    resource: Resource
    selectedAsset?: Asset
    display: boolean = false
    isLBPage: boolean = false
    customActionGTMClassName: string = ""

    windowResizeObserver: Observable<IWindowResizeEvent> = this.windowSizeService.resize.pipe(
        takeUntil(this.destroyed$),
        tap((next: IWindowResizeEvent) => (this.isTablet = next.isTablet)),
        tap((next: IWindowResizeEvent) => (this.innerWidth = next.innerWidth))
    )

    resourceChangeObserver: Observable<Resource> = this.resourceService.resourceResponse.pipe(
        takeUntil(this.destroyed$),
        tap((resource: Resource) => this.updateResource(resource))
    )

    geoRestrictedObservable: Observable<boolean> = this.resourceService
        .getResourceGeRestrictedState()
        .pipe(
            takeUntil(this.destroyed$),
            tap((state: boolean) => {
                this.display = !state && this.cautionFlagsService.isContentBoxDisplayed
            })
        )

    constructor(
        private cautionFlagsService: CautionFlagsService,
        private indexedDBService: IndexedDBService,
        private windowSizeService: WindowSizeService,
        private resourceService: ResourceService,
        private lessonBuilderService: LessonBuilderService
    ) {}

    ngOnInit() {
        this.windowResizeObserver.subscribe()
        this.resourceChangeObserver.subscribe()
        this.geoRestrictedObservable.subscribe()
        this.isLBPage = this.isLessonBuilderPage
        if (this.isLBPage) this.actionButtonName = "View Media"
        this.customActionGTMClassName = this.customActionClass
    }

    ngOnChanges() {
        this.updateSelectedAsset()
    }

    get isVideo() {
        return (
            this.resource?.opensAsVideo ||
            this.selectedAsset?.mediaType === "Video" ||
            this.lessonBuilderService?.selectedAsset?.mediaType === "Video"
        )
    }

    updateResource(resource: Resource) {
        this.resource = resource
        this.updateSelectedAsset()
    }

    get resourceOpensInNewTab() {
        return resourcesToOpenInNewTab.includes(this.resource.mediaType)
    }

    updateSelectedAsset() {
        if (!this.resource?.guid) return
        if (this.resource.opensAsMediaGallery && this.selectedAsset !== this.resource.selectedAsset)
            this.selectedAsset =
                this.resource.selectedAsset || this.lessonBuilderService.selectedAsset
    }

    toggleCautionTab() {
        if (this.isLBPage) {
            window.open(this.lessonBuilderService.previewResource.link, "_blank")
            this.resourceService.activeTab = "Cautions"
        } else {
            this.cautionFlagsService.cautionFlagTabToggle = true
            const element: HTMLElement = document.getElementById("nav-tab")
            if (element) element.scrollIntoView({ behavior: "smooth" })
        }
    }

    get customActionClass(): string {
        return "gtm-" + this.actionButtonName.replace(" ", "-").toLowerCase() + "-button"
    }

    closeContainer(action: boolean = false) {
        if (this.customAction && action) this.customActionTrigger.emit(true)
        this.cautionFlagsService.cautionFlagDisplay = false
    }

    dismissCautionContainer() {
        this.indexedDBService.excludeCautionFlagsForResource(
            this.resourceMediaType,
            this.resourceCode
        )
        this.closeContainer()
    }

    public get resourceMediaType() {
        if (this.resource?.mediaType) return this.resource?.mediaType
        if (!!this.lessonBuilderService?.previewResource)
            return this.lessonBuilderService?.previewResource?.assets.length > 1
                ? "Media Gallery"
                : this.lessonBuilderService.previewResource.assets[0].mediaType
    }

    private get isLessonBuilderPage(): boolean {
        return window.location.pathname.startsWith("/tools/lessonbuilder/")
    }

    private get resourceCode(): string {
        if (!this.resource.mediaType) {
            let resourceCode: string =
                this.lessonBuilderService.previewResource.creditsLink.replace("/credits/", "")
            return (
                resourceCode +
                (this.resourceMediaType === "Media Gallery"
                    ? "/" + this.lessonBuilderService.selectedAsset.guid
                    : "")
            )
        }

        return this.resource.opensAsMediaGallery
            ? this.resource.resourceCode + "/" + this.selectedAsset.guid
            : this.resource.resourceCode
    }
}
